import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
/**
 * Buxgalteriya hujjatlari — SO'ROVNOMA (`S-2026-0001`) va PRIXOD
 * (`X-2026-0001`).
 *
 * Ikkalasi ham FAQAT talabnoma o'tishida tug'iladi (state machine effekti), ya'ni
 * yaratish uchun hech qanday `POST` yo'q va bo'lmaydi ham: hujjat talabnomadan
 * ajralib, o'z-o'zicha paydo bo'la olmasligi kerak. Shu sababli bu faylda
 * faqat O'QISH shakllari bor.
 */
const idRef = z.string().regex(/^\d+$/, 'Identifikator raqamlardan iborat bo\'lishi kerak');
/**
 * So'rovnoma HOLATI.
 *
 * `PENDING` — buxgalter navbatida; `APPROVED` — tasdiqlangan va undan prixod
 * tug'ilgan; `REJECTED` — rad etilgan.
 */
export const SURVEY_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const surveyStatusSchema = z.enum(SURVEY_STATUSES);
const SURVEY_STATUS_LABELS = {
    PENDING: 'Buxgalter tasdig\'ini kutmoqda',
    APPROVED: 'Tasdiqlangan',
    REJECTED: 'Rad etilgan',
};
export const surveyStatusLabel = (status) => SURVEY_STATUS_LABELS[status];
/**
 * Bosma hujjatning TILI.
 *
 * Ilova tili mijozda saqlanadi (`localStorage`), PDF esa SERVERDA
 * yasaladi — ya'ni til so'rov bilan yuborilishi kerak. Ro'yxat mijoz
 * lug'atlari bilan bir xil va shartnomada turadi: ikki joyda takrorlangan
 * ro'yxat bir kun kelib bir-biridan farq qilib qolardi.
 */
export const DOC_LOCALES = ['uz', 'ru', 'en'];
export const docLocaleSchema = z.enum(DOC_LOCALES);
/**
 * ФМУ №25 blanki UCHUN boshlang'ich til — RUSCHA.
 *
 * Blank 1972 yilda tasdiqlangan va buxgalterning qog'oz dastasi ruscha.
 * Ilova o'zbekcha ishlayotgan bo'lsa ham, chop etishga ketadigan qog'oz
 * o'sha dastaga tushadi va ustunlar bir-biriga mos kelishi kerak.
 */
export const DEFAULT_FORM_LOCALE = 'ru';
export const surveySchema = z.object({
    id: z.string(),
    docNo: z.string(),
    /**
     * Qog'ozdagi «ТРЕБОВАНИЕ №» — BUXGALTER yozadi.
     *
     * `docNo` tizimning ichki raqami: u so'rovnoma tug'ilganda, skladchining
     * tranzaksiyasida beriladi va o'zgarmaydi — havolalar, jurnal va qidiruv
     * shunga tayanadi. Blankdagi raqam esa buxgalterning O'Z daftaridan
     * keladi va tizimning sanog'iga bo'ysunmaydi.
     *
     * Bo'sh bo'lsa blankda ichki raqam chiqadi.
     */
    formNo: z.string().nullable(),
    status: surveyStatusSchema,
    requisitionId: z.string(),
    /** Talabnoma raqami hujjatga KO'CHIRILADI — qog'ozdagi hujjat o'z-o'zicha o'qilishi kerak. */
    requisitionDocNo: z.string(),
    warehouseId: z.string(),
    mtuId: z.string(),
    orgUnitId: z.string().nullable(),
    createdBy: z.string(),
    createdAt: z.string(),
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    rejectedReason: z.string().nullable(),
    /**
     * «Затребовал» — talabnomani YOZGAN xodim.
     *
     * `createdBy` (so'rovnomani ochgan skladchi) bilan adashtirmaslik kerak:
     * ФМУ №25 blankida ular ikki xil chiziq va ko'pincha ikki xil odam —
     * ariza korxonadan keladi, so'rovnoma esa ombordan.
     */
    requesterUserId: z.string().nullable(),
    /** «Через кого» — mahsulotni olib ketadigan odam. */
    throughPerson: z.string().nullable(),
    /** Talabnoma maqsadi — blankning izoh qatorida. */
    purpose: z.string().nullable(),
});
/**
 * Blankdagi raqamni yozish — buxgalterning yagona tahriri.
 *
 * Faqat `PENDING` so'rovnomada: tasdiqlangan hujjat o'zgarmaydi va
 * qog'ozdagi raqam ham. `null` — raqamni olib tashlash, ya'ni blankda
 * yana ichki raqam chiqadi.
 *
 * Shakl ERKIN: buxgalterning daftari tizimniki emas va u yerdagi raqam
 * `S-2026-0005` shaklida bo'lishi shart emas. Tekshiriladigan yagona
 * narsa — uzunligi va bo'sh joy emasligi.
 */
export const updateSurveySchema = z.object({
    formNo: z
        .string()
        .trim()
        .min(1, 'Raqam bo\'sh bo\'lishi mumkin emas')
        .max(60, 'Raqam 60 belgidan oshmasligi kerak')
        .nullable(),
});
/** So'rovnoma satri — ФМУ №25 blankining jadvali. */
export const surveyLineSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    nomenclatureId: z.string(),
    /** «Затребовано» — so'ralgan miqdor. «Отпущено» blankda bo'sh qoladi. */
    qty: z.number(),
});
export const surveyWithLinesSchema = surveySchema.extend({
    lines: z.array(surveyLineSchema),
});
export const incomingDocLineSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    nomenclatureId: z.string(),
    qty: z.number(),
    batchId: z.string().nullable(),
    /**
     * Partiya raqami hujjatga KO'CHIRILADI (havola emas, nusxa): rasmiy hujjatda
     * yozilgan raqam keyinchalik partiya yozuvi o'zgarsa ham o'zgarmasligi kerak.
     */
    batchNo: z.string().nullable(),
});
export const incomingDocSchema = z.object({
    id: z.string(),
    docNo: z.string(),
    surveyId: z.string(),
    requisitionId: z.string(),
    requisitionDocNo: z.string(),
    warehouseId: z.string(),
    mtuId: z.string(),
    /** QABUL QILUVCHI korxona — prixod aynan shu korxonaning hisobchisiga tushadi. */
    orgUnitId: z.string().nullable(),
    accountantUserId: z.string().nullable(),
    /** 2-etap: 1C ga uzatilganda tashqi tizimdagi havola shu yerga yoziladi. */
    externalRef: z.string().nullable(),
    createdAt: z.string(),
    lines: z.array(incomingDocLineSchema),
});
export const surveyQuerySchema = paginationQuerySchema.extend({
    status: surveyStatusSchema.optional(),
    requisitionId: idRef.optional(),
});
export const incomingDocQuerySchema = paginationQuerySchema.extend({
    orgUnitId: idRef.optional(),
    requisitionId: idRef.optional(),
});
//# sourceMappingURL=accounting.schema.js.map